import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CALL_STATUS_OPTIONS, CALL_STATUS_TONE, type CallStatus, type FollowUpTone } from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { getErrorMessage } from '@/lib/errors';
import { TONE_TEXT } from '@/lib/tone';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/** Compact trigger shared by the follow-up dropdowns (table cells and detail dialog). */
export const FOLLOW_UP_TRIGGER =
  'h-8 w-full min-w-[11.5rem] gap-2 whitespace-nowrap rounded-lg px-2.5 py-1 text-left text-xs font-medium sm:h-8 sm:px-2.5 sm:py-1 focus:ring-1 focus:ring-offset-0';

/**
 * An option's label in its status colour, with a dot. Used inside SelectItem, so
 * the trigger (which mirrors the chosen item's text) is coloured the same way.
 */
export function ToneLabel({ tone, children }: { tone: FollowUpTone; children: React.ReactNode }) {
  return (
    <span className={cn('inline-flex items-center gap-2 font-medium', TONE_TEXT[tone])}>
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden />
      {children}
    </span>
  );
}

/** Keeps the item's status colour when it is highlighted (the base item recolours on focus). */
export const TONE_ITEM = 'text-xs focus:bg-muted focus:text-inherit sm:py-1.5';

/**
 * The call status as a dropdown: picking a new status logs the call at once, no
 * remark needed. Remarks are added from the entry's detail dialog.
 *
 * Sits inside a clickable table row; clicks (including inside the portalled
 * list, which still bubble through React) stop at the wrapper so picking an
 * option does not also open the row.
 */
export function CallStatusSelect({
  id,
  status,
  disabled = false,
  className,
}: {
  id: string;
  status: CallStatus;
  disabled?: boolean;
  className?: string;
}) {
  const queryClient = useQueryClient();
  // The pick shown until the server's answer is back (optimistic), then the prop again.
  const [picked, setPicked] = useState<CallStatus | null>(null);
  const value = picked ?? status;

  const save = useMutation({
    mutationFn: (next: CallStatus) => trackerFdfcService.logCall(id, { status: next }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] });
      setPicked(null);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      setPicked(null);
    },
  });

  return (
    <span className="block" onClick={(e) => e.stopPropagation()}>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(v) => {
          const next = v as CallStatus;
          if (next === value) return;
          setPicked(next);
          save.mutate(next);
        }}
      >
        <SelectTrigger aria-label="Call status" className={cn(FOLLOW_UP_TRIGGER, className)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CALL_STATUS_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value} className={TONE_ITEM}>
              <ToneLabel tone={CALL_STATUS_TONE[o.value]}>{o.label}</ToneLabel>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </span>
  );
}
